/**
 * ตัวหน่วงการ commit (debounce) แบบไม่ผูกกับ React — สกัดออกมาเพื่อให้เทสต์ด้วย fake timer ได้
 *
 * ใช้กับช่องกรอกที่ commit แล้วมีราคาแพง (เขียน localStorage + คำนวณ aggregate/กราฟใหม่ทั้งชุด)
 * `push` เก็บค่าล่าสุดไว้แล้วเลื่อนเวลาออกไป · `flush` ส่งทันที (blur/Enter/unmount) · `cancel` ทิ้งค่าที่ค้าง
 */
export type DebouncedCommit<T> = {
  /** ตั้งค่าใหม่ที่จะ commit และเลื่อนเวลาออกไปอีก delayMs */
  push: (value: T) => void;
  /** ส่งค่าที่ค้างทันที (ถ้ามี) */
  flush: () => void;
  /** ทิ้งค่าที่ค้างโดยไม่ส่ง */
  cancel: () => void;
  /** ยังมีค่าที่รอส่งอยู่ไหม */
  hasPending: () => boolean;
};

export const createDebouncedCommit = <T>(commit: (value: T) => void, delayMs: number): DebouncedCommit<T> => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: { value: T } | null = null;

  const clear = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const send = () => {
    const item = pending;
    pending = null;
    if (item) commit(item.value);
  };

  return {
    push: (value: T) => {
      pending = { value };
      clear();
      timer = setTimeout(() => {
        timer = null;
        send();
      }, delayMs);
    },
    flush: () => {
      clear();
      send();
    },
    cancel: () => {
      clear();
      pending = null;
    },
    hasPending: () => pending !== null,
  };
};
